import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent } from '@material-ui/core';
import { TrendingUp, PlayCircleOutline, CheckCircle, Schedule } from '@material-ui/icons';
import videoProgressService from '../../services/videoProgressService';
import './OverallProgressDashboard.scss';

const OverallProgressDashboard = ({ userId, className = '' }) => {
  const [overallProgress, setOverallProgress] = useState({
    totalVideos: 0,
    completedVideos: 0,
    inProgress: 0,
    completionRate: 0,
    averageProgress: 0,
    totalWatchTime: '00:00:00',
    progressByCategory: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOverallProgress = async () => {
      try {
        setLoading(true);
        const progressData = await videoProgressService.getUserOverallProgress(userId);
        setOverallProgress(progressData);
      } catch (error) {
        console.error('Error loading overall progress:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadOverallProgress();
    }
  }, [userId]);

  const getCategoryColor = (category) => {
    const colors = {
      'Incepator': '#4caf50',
      'Intermediar': '#ff9800', 
      'Avansat': '#f44336',
      'Expert': '#9c27b0',
      'default': '#904d71'
    };
    return colors[category] || colors.default;
  };

  if (loading) {
    return (
      <Box className="overall-progress-loading">
        <Typography>Se încarcă progresul...</Typography>
      </Box>
    );
  }

  return (
    <Box className={`overall-progress-dashboard ${className}`}>
      {/* Main Progress Overview */}
      <Card className="overview-progress-container">
        <CardContent>
          <Box className="overview-header">
            <Box className="overview-title-section">
              <TrendingUp className="overview-icon" />
              <Typography variant="h6" className="overview-title">
                Progresul General
              </Typography>
            </Box>
            <Typography variant="h4" className="overview-percentage">
              {Math.round(overallProgress.completionRate)}%
            </Typography>
          </Box>

          <Box className="overview-progress-bar">
            <Box 
              className="overview-progress-fill"
              style={{ width: `${overallProgress.completionRate}%` }}
            />
          </Box>

          <Grid container spacing={2} className="overview-stats">
            <Grid item xs={6} sm={3}>
              <Box className="stat-item">
                <Typography variant="h6" className="stat-value">
                  {overallProgress.totalVideos}
                </Typography>
                <Typography variant="caption" className="stat-label">
                  Total Videoclipuri
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box className="stat-item">
                <Typography variant="h6" className="stat-value">
                  {overallProgress.completedVideos}
                </Typography>
                <Typography variant="caption" className="stat-label">
                  Completate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box className="stat-item">
                <Typography variant="h6" className="stat-value">
                  {overallProgress.inProgress}
                </Typography>
                <Typography variant="caption" className="stat-label">
                  În Progres
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box className="stat-item">
                <Typography variant="h6" className="stat-value">
                  {overallProgress.totalWatchTime}
                </Typography>
                <Typography variant="caption" className="stat-label">
                  Timp Total
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Category Progress */}
      {Object.keys(overallProgress.progressByCategory).length > 0 && (
        <Card className="category-progress-container">
          <CardContent>
            <Box className="category-header">
              <Typography variant="h6" className="category-title">
                Progres pe Categorii
              </Typography>
            </Box>

            <Box className="category-progress-list">
              {Object.entries(overallProgress.progressByCategory).map(([category, stats]) => (
                <Box key={category} className="category-progress-item">
                  <Typography className="category-name">
                    {category}
                  </Typography>
                  
                  <Box className="category-progress-bar">
                    <Box 
                      className="category-progress-fill"
                      style={{ 
                        width: `${stats.completionRate}%`,
                        backgroundColor: getCategoryColor(category)
                      }}
                    />
                  </Box>
                  
                  <Typography className="category-percentage">
                    {Math.round(stats.completionRate)}%
                  </Typography>
                  
                  <Box className="category-stats">
                    <Typography variant="caption">
                      {stats.completed}/{stats.total}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Achievement Cards */}
      <Grid container spacing={2} className="achievement-cards">
        <Grid item xs={12} sm={4}>
          <Card className="achievement-card completion">
            <CardContent>
              <Box className="achievement-content">
                <CheckCircle className="achievement-icon" />
                <Box>
                  <Typography variant="h6" className="achievement-value">
                    {Math.round(overallProgress.averageProgress)}%
                  </Typography>
                  <Typography variant="caption" className="achievement-label">
                    Progres Mediu
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card className="achievement-card progress">
            <CardContent>
              <Box className="achievement-content">
                <PlayCircleOutline className="achievement-icon" />
                <Box>
                  <Typography variant="h6" className="achievement-value">
                    {overallProgress.inProgress}
                  </Typography>
                  <Typography variant="caption" className="achievement-label">
                    Videoclipuri Începute
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card className="achievement-card time">
            <CardContent>
              <Box className="achievement-content">
                <Schedule className="achievement-icon" />
                <Box>
                  <Typography variant="h6" className="achievement-value">
                    {overallProgress.totalWatchTime}
                  </Typography>
                  <Typography variant="caption" className="achievement-label">
                    Timp de Vizionare
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverallProgressDashboard;
